<template>
  <div>
    <div class="p-5 flex flex-col">
      <span class="font-semibold text-3xl">List User Management</span>
      <vs-row
        vs-w="12"
        class="my-5 filter-container"
      >
        <vs-col vs-w="2">
          <p class="font-semibold">
            Filter
          </p>
        </vs-col>
        <vs-col vs-w="2">
          <select-with-search
            hide-search
            v-model="filter.active"
            placeholder="Select is active"
            class="px-2"
            :options="stateOptions"
          />
        </vs-col>
        <vs-col vs-w="2">
          <select-with-search
            hide-search
            v-model="filter.is_active_directory"
            placeholder="Select active directory"
            class="px-2"
            :options="stateOptions"
          />
        </vs-col>
        <vs-col vs-w="2">
          <select-with-search
            v-model="filter.company_id"
            placeholder="Select companies"
            class="px-2"
            :options="companyOptions"
          />
        </vs-col>
        <vs-col vs-w="2">
          <select-with-search
            v-model="filter.group_id"
            placeholder="Select groups"
            class="px-2"
            :options="groupOptions"
          />
        </vs-col>
        <vs-col
          vs-w="2"
          class="flex flex-col justify-center"
        >
          <vs-button
            type="filled"
            color="primary"
            class="px-2 w-40 self-center font-semibold"
            @click="fetchUsers"
          >
            Filter
          </vs-button>
        </vs-col>
      </vs-row>
      <table-top
        :page-size-start="false"
        @fetch="fetchUsers"
      >
        <template #start>
          <vs-button
            v-show="isUserCanAccessMenu(permission.name, 'add')"
            type="filled"
            color="primary"
            class="w-40 font-semibold"
            @click="$router.push({ name: 'AuthorizationUserManagementCreate' })"
          >
            Add User
          </vs-button>
        </template>
      </table-top>
      <data-table
        class="my-5"
        :table-header="userHeaders"
        :table-data="userData"
        :loading="isLoading"
        :sort-by="filter.sort_by"
        :sort-dir="filter.sort_dir"
        body-height="100vh"
        @sort="sortTable"
      >
        <template #action="{ row }">
          <div class="w-24 flex flex-row">
            <img
              :src="require('../../../../../assets/shipo-icon-see.svg')"
              class="w-8 icon-transparent-edit-bg"
              @click="$router.push({ name: 'AuthorizationUserManagementDetail', params: { id: row.id }})"
            >
            <img
              v-show="isUserCanAccessMenu(permission.name, 'edit')"
              :src="require('../../../../../assets/shipo-icon-edit.svg')"
              class="mx-1 w-8 icon-transparent-edit-bg"
              @click="$router.push({ name: 'AuthorizationUserManagementEdit', params: { id: row.id } })"
            >
            <img
              v-show="isUserCanAccessMenu(permission.name, 'delete')"
              :src="require('../../../../../assets/shipo-icon-delete.svg')"
              class="w-8 cursor-pointer icon-transparent-edit-bg"
              @click="showDeleteModal(row.id)"
            >
          </div>
        </template>
        <template #active="{ row }">
          <vs-switch
            v-model="row.active"
            color="secondary"
            :disabled="isSwitching || !isUserCanAccessMenu(permission.name, 'is_active')"
          />
        </template>
        <template #is_active_directory="{ row }">
          <img :src="require(`../../../../../assets/shipo-icon-${row.is_active_directory ? 'checkmark' : 'close'}.svg`)">
        </template>
        <template #notification="{ row }">
          <img :src="require(`../../../../../assets/shipo-icon-${row.notification ? 'checkmark' : 'close'}.svg`)">
        </template>
        <template #name="{ row }">
          <span>{{ row.name || '-' }}</span>
        </template>
        <template #title="{ row }">
          <span>{{ row.title || '-' }}</span>
        </template>
        <template #companies="{ row }">
          <div class="w-40">
            <span v-if="!row.companies.length">-</span>
            <div
              v-else
              class="w-full grid grid-flow-col grid-flow-row gap-1 company-list"
            >
              <span
                v-for="(item, index) of row.companies.slice(0, 2)"
                :key="index"
                class="text-primary whitespace-pre-wrap pill pill-primary truncate tooltip"
              >
                {{ item.name }}
                <p class="tooltiptext">
                  {{ item.name }}
                </p>
              </span>
              <div
                v-if="row.companies.length > 2"
              >
                <div class="text-primary whitespace-pre-line pill pill-primary tooltip">
                  <span class="">
                    ...
                  </span>
                  <div class="tooltiptext">
                    <ol class="list-disc">
                      <li
                        v-for="(item) of row.companies.slice(2)"
                        :key="item.name"
                      >
                        {{ item.name }}
                      </li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
        <template #groups="{ row }">
          <div class="w-48 flex flex-row flex-wrap flex-gap">
            <span v-if="!row.groups.length">-</span>
            <span
              v-else
              v-for="(item, index) of row.groups"
              class="mr-1 text-danger pill pill-danger"
              :key="index"
            >{{ item.name }}</span>
          </div>
        </template>
      </data-table>
      <table-bottom @fetch="fetchUsers" />
    </div>
    <pop-up
      :active.sync="isPopUpShown"
      :is-warning="isDeleting"
      :title="isDeleting ? 'Are you sure?' : 'Successfully'"
      :left-button="`Delete`"
      :right-button="`Cancel`"
      @ok="isPopUpShown = !isPopUpShown"
      @close="isPopUpShown = !isPopUpShown"
      @leftButton="deleteUser"
      @rightButton="isPopUpShown = !isPopUpShown"
    >
      {{ isDeleting ? 'Do you really want to delete this record' : 'You have successfully delete user' }}
    </pop-up>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import DataTable from '@/components/dataTable'
import PopUp from '@/components/popUp'
import SelectWithSearch from '@/components/selectWithSearch'
import TableBottom from '@/components/tableBottom'
import TableTop from '@/components/tableTop'

export default {
  components: {
    DataTable,
    PopUp,
    SelectWithSearch,
    TableBottom,
    TableTop
  },
  data() {
    return {
      isLoading: false,
      isDeleting: false,
      isPopUpShown: false,
      isSwitching: false,
      userId: null,
      filter: {
        active: null,
        is_active_directory: null,
        notification: null,
        company_id: null,
        group_id: null,
        sort_by: 'username',
        sort_dir: 'asc'
      },
      userHeaders: [
        { key: 'action', label: 'Action' },
        { key: 'active', label: 'Is Active', sortable: true },
        { key: 'username', label: 'Login Name', sortable: true },
        { key: 'mail', label: 'Email' },
        { key: 'is_active_directory', label: 'Active Directory', sortable: true },
        { key: 'notification', label: 'Notification', sortable: true },
        { key: 'name', label: 'Name', sortable: true },
        { key: 'title', label: 'Title', sortable: true },
        { key: 'companies', label: 'Companies' },
        { key: 'groups', label: 'Groups' }
      ],
      userData: [],
      cloneUserData: [],
      stateOptions: [
        { value: 'true', text: 'Yes' },
        { value: 'false', text: 'No' }
      ],
      companyOptions: [],
      groupOptions: []
    }
  },
  computed: {
    ...mapGetters({
      users: 'User/users',
      companies: 'MonitoringReport/companies',
      groups: 'Group/groups'
    }),
    permission() {
      return { page: this.$route.meta.permission.page, name: this.$route.meta.permission.name }
    }
  },
  watch: {
    users: {
      handler(val) {
        this.userData = val
        this.cloneUserData = this.cloneDeep(this.userData)
      }
    },
    companies: {
      handler(val) {
        this.companyOptions = val.map(item => ({ value: item.id, text: item.name }))
      }
    },
    groups: {
      handler(val) {
        this.groupOptions = val.map(item => ({ value: item.id, text: item.name }))
      }
    },
    userData: {
      deep: true,
      async handler(val) {
        const diff = this.differenceWith(val, this.cloneUserData, this.isEqual)
        if (diff.length) {
          const item = diff[0]
          await this.switchUserStatus(item)
        }
      }
    }
  },
  mounted() {
    this.fetchCompanies()
    this.fetchGroups()
  },
  methods: {
    async fetchCompanies() {
      await this.$store.dispatch('MonitoringReport/fetchCompanies')
    },
    async fetchGroups() {
      await this.$store.dispatch('Group/fetchGroups', { isActiveAll: true })
    },
    async fetchUsers() {
      try {
        this.isLoading = true

        await this.$store.dispatch('User/fetchUsers', this.filter)

        this.isLoading = false
      } catch (err) {
        this.isLoading = false
        this.showFailedNotification(this)
      }
    },
    async deleteUser() {
      try {
        await this.$store.dispatch('User/deleteUser', this.userId)

        this.isDeleting = false

        await this.fetchUsers()
      } catch (err) {
        this.isDeleting = false
        this.isPopUpShown = false
        this.showFailedNotification(this)
      }
    },
    async switchUserStatus(data) {
      try {
        this.isSwitching = true

        await this.$store.dispatch('User/putUser', { id: data.id, active: data.active })

        this.isSwitching = false
        this.showSuccessNotification(this, 'You have successfully update user state')

        await this.fetchUsers()
      } catch (err) {
        this.isSwitching = false
        this.showFailedNotification(this)
      }
    },
    showDeleteModal(id) {
      this.userId = id
      this.isDeleting = true
      this.isPopUpShown = true
    },
    sortTable(sortBy, sortDir) {
      this.filter.sort_by = sortBy
      this.filter.sort_dir = sortDir

      this.fetchUsers()
    }
  }
}
</script>

<style lang="scss" scoped>
.filter-container {
  @apply p-5 mt-5 flex flex-row items-center bg-white rounded-lg;
}

.pill {
  @apply px-3 py-1 font-semibold;

  &-primary {
    @apply rounded-2xl;
    background-color: rgba(#CDC6E1, 0.35);
  }

  &-danger {
    @apply rounded-lg;
    background-color: rgba(255, 100, 124, 0.2);
  }
}

.icon-transparent-edit-bg {
  @apply rounded-lg cursor-pointer;
  @apply bg-primary;
}

.flex-gap {
  grid-row-gap: 10px;
}

.vs-switch-secondary.vs-switch-active {
  @apply bg-secondary-accent;

  /deep/.vs-switch--circle {
    @apply bg-secondary;
  }
}

/deep/.vs-switch--circle {
  @apply bg-grey-001;
}

.tooltiptext {
  @apply invisible p-2 bg-white bg-opacity-75 font-medium text-primary rounded-lg;
  width: fit-content;
  max-width: 20rem;
  text-align: justify;
  text-wrap: pretty;
  position: absolute;
  z-index: 1;
  backdrop-filter: blur(6px);
}

.tooltip:hover .tooltiptext {
  @apply visible;
}
</style>
